import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { brandText } from '../../../../config/branding';
import { chartTimePeriod } from '../../../../config/enum';
import currentTimeZone from '../../../../config/timeZone';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import dashboard from '../../../../utils/api/partner/dashboard';

interface ChartDataProperties {
  label: string;
  [key: string]: string;
}

/**
 * Component to display revenue vs power consumed chart
 * @returns {any} The result
 */
export default function RevenuePowerConsumedChart() {
  const { t } = useTranslation('dashboard');
  const [powerConsumedData, setPowerConsumedData] = useState<ChartDataProperties[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const activeRangeReference = React.useRef<
    'last24hours' | 'last7days' | 'lastMonth' | 'last12months'
  >('last24hours');

  const chartData = {
    labels: powerConsumedData.map((d) => d.label),
    datasets: [
      {
        label: t('revenue'),
        data: powerConsumedData.map((d) => d.totalDebitAmount || 0),
        borderColor: '#F79009',
        /**
         * Background
         * @param {any} context - The context
         * @returns {any} The result
         */
        backgroundColor: (context: any) => {
          const { chart } = context;
          const contentContext = chart.ctx;
          const gradient = contentContext.createLinearGradient(0, 0, 0, chart.height);
          gradient.addColorStop(0, 'rgba(247, 144, 9, 0.4)');
          gradient.addColorStop(1, 'rgba(237, 237, 237, 0)');
          return gradient;
        },
        fill: 'origin', // Fills to the previous dataset
        tension: 0.4,
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: t('electricity'),
        data: powerConsumedData.map((d) => d.totalPowerConsumed || 0),
        borderColor: '#2E9E3A',
        /**
         * Function to create a gradient bg color
         * @param {any} context - The context
         * @returns {any} The result
         */
        backgroundColor: (context: any) => {
          const { chart } = context;
          const chartContext = chart.ctx;
          const gradient = chartContext.createLinearGradient(0, 0, 1, chart.height);
          gradient.addColorStop(0, 'rgba(46, 158, 58, 1)');
          gradient.addColorStop(1, 'rgba(237, 237, 237, 0)');
          return gradient;
        },
        fill: 'origin',
        tension: 0.4,
        borderWidth: 1,
        pointRadius: 0, // Remove points on the line
        pointHoverRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const, // Explicitly cast to 'top' to ensure correct type
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 10,
          padding: 15,
        },
        padding: {
          right: 500, // Add 50px padding to the right side of the legend
        },
      },
      tooltip: {
        /**
         * Function to crete tooltip bg color
         * @param {any} context - The context
         * @returns {any} The result
         */
        backgroundColor: (context: any) => {
          if (context?.tooltipItems?.[0]?.dataset) {
            // Check the dataset label and apply different colors
            if (context.tooltipItems[0].dataset.label === t('revenue')) {
              return 'rgba(247, 144, 9, 0.9)'; // Orange for Revenue
            }
            if (context.tooltipItems[0].dataset.label === t('electricity')) {
              return 'rgba(46, 158, 58, 0.9)'; // Green for Electricity
            }
          }
          return 'rgba(0, 0, 0, 0.8)'; // Default color
        },
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          /**
           * Function to generate to title for chart
           * @param {any} context - The context
           * @returns {any} The result
           */
          title: (context: any) => {
            // Set title based on dataset
            const datasetLabel = context[0]?.dataset?.label;
            return datasetLabel === t('revenue') ? t('revenue') : t('electricity');
          },
          /**
           * Function to show tooltip data
           * @param {any} context - The context
           * @returns {any} The result
           */
          label: (context: any) => {
            // Format the label with currency
            const datasetLabel = context?.dataset?.label;
            return `${context?.raw.toLocaleString()}  ${datasetLabel === t('revenue') ? brandText.currency : 'Kwh'}`;
          },
        },
      },
    },

    interaction: {
      intersect: false, // Don't require an exact point to show the tooltip
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false, // Hides the y-axis line
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: false, // Hides the tick labels on the y-axis (revenue and expense values)
        },
        border: {
          display: false, // Hides the y-axis line
        },
      },
    },
  };

  /**
   * Fetch power consumed data based on time range
   * @param {any} range - The range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    activeRangeReference.current = range;
    try {
      setIsLoading(true);
      const response = await dashboard.getPowerConsumedReport({
        timePeriod: range,
        timeZone: currentTimeZone,
      });

      // ❌ If user already switched to another range, ignore this response
      if (activeRangeReference.current !== range) return;

      // ✅ Otherwise, update the chart with the right data
      if (range === 'last12months' && response.status === 200) {
        setPowerConsumedData(fillMissingMonthsGeneric(response.data));
      } else if (range === 'lastMonth' && response.status === 200) {
        setPowerConsumedData(fillMissingLastNDaysGeneric(response.data, 30));
      } else if (range === 'last7days' && response.status === 200) {
        setPowerConsumedData(fillMissingLastNDaysGeneric(response.data, 7));
      } else if (range === 'last24hours' && response.status === 200) {
        setPowerConsumedData(fillMissingLast24HrsGeneric(response.data));
      }
      setIsLoading(false);
    } catch (error) {
      console.log(error);

      // only stop loading if this request is still the active one
      if (activeRangeReference.current === range) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, []);

  return (
    <div className="revenue-power-consumer-chart-wrap-partner">
      <div className="revenue-power-consumer-chart-header">
        <div className="header-title">
          <h6>
            <span className="power-consumer">{t('powerConsumed')}</span>
            <span>{t('vs')}</span>
            <span className="revenue-growth">{t('revenueGrowth')}</span>
          </h6>
          {/* <span className="header-arrow">{'->'}</span> */}
        </div>
        <CustomToggleButton
          labels={chartTimePeriod.map((duration) => t(duration.label))}
          onToggle={(index, label) => {
            const selectedDuration = chartTimePeriod.find(
              (duration) => t(duration.label) === label,
            );
            fetchPowerConsumedReport(selectedDuration?.value || 'last24hours');
            setIsLoading(true);
          }}
          defaultActiveIndex={0}
          type="chart"
        />
      </div>
      <div className="revenue-power-consumer-chart-container">
        {isLoading ? (
          <div className="revenue-power-consumed-loader">
            <BatteryLoader />
          </div>
        ) : (
          <Line data={chartData} options={options} />
        )}
      </div>
    </div>
  );
}
